import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trophy, Star, ArrowRight, Timer, Download, WifiOff, CloudUpload, CheckCircle2 } from "lucide-react";
import jsPDF from "jspdf";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { EXAM } from "@/constants/testIds";
import { Carlos, Mascot } from "@/components/characters/Characters";
import { CanalesLogo } from "@/components/CanalesLogo";
import { LangToggle } from "@/components/layout/Nav";
import { BadgePill, TierChip } from "@/components/Badges";
import {
  saveExamCache,
  getExamCache,
  enqueueSubmission,
  queueCount,
  syncPending,
} from "@/lib/offline";
const AvatarMentorFeedback = ({ item, lang, t }) => {
  const opts = item.options || [];
  const correctOpt = opts.find((o) => o.id === item.correct);
  const selectedOpt = opts.find((o) => o.id === item.selected);
  return (
    <div className={`brutal p-4 sm:p-5 my-4 ${item.was_correct ? "bg-green-50" : "bg-red-50"}`}>
      <div className="flex items-start gap-3">
        <Carlos size={80} mood={item.was_correct ? "correct" : "wrong"} />
        <div className="flex-1">
          <div className={`font-display font-black text-lg ${item.was_correct ? "text-green-700" : "text-red-700"}`}>
            {item.was_correct ? t("correct") : t("incorrect")}
          </div>
          <div className="font-body mt-1">
            <div className="text-sm">
              <span className="font-bold">{t("yourAnswer")} </span>
              {selectedOpt ? `${selectedOpt.id}. ${selectedOpt[lang]}` : t("unanswered")}
            </div>
            {!item.was_correct && (
              <div className="text-sm mt-1">
                <span className="font-bold text-green-700">{t("correctAnswer")} </span>
                {correctOpt ? `${correctOpt.id}. ${correctOpt[lang]}` : ""}
              </div>
            )}
            <div className="mt-3 brutal-sm bg-white p-3">
              <div className="text-xs font-display font-bold text-canales-red uppercase">
                {t("story")}
              </div>
              <p className="text-sm mt-1">{item[`explanation_${lang}`]}</p>
              <p className="text-sm italic mt-1 text-neutral-700">&ldquo;{item[`mini_story_${lang}`]}&rdquo;</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatSecs = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
};

export default function ExamPublic() {
  const { slug } = useParams();
  const { t, lang } = useLang();

  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState(() => localStorage.getItem("canales_vendor_name") || "");
  const [store, setStore] = useState(() => localStorage.getItem("canales_vendor_store") || "");
  const [managerName, setManagerName] = useState(() => localStorage.getItem("canales_vendor_manager") || "");
  const [started, setStarted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const [remaining, setRemaining] = useState(null);
  const timerRef = useRef(null);

  const [isOnline, setIsOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [fromCache, setFromCache] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [queuedOffline, setQueuedOffline] = useState(false);

  // Roster loaded once for autocomplete on Tienda + Manager inputs.
  const [roster, setRoster] = useState({ stores: [], managers: [], names: [] });
  useEffect(() => {
    let cancelled = false;
    api
      .get("/public/roster")
      .then(({ data }) => { if (!cancelled) setRoster(data || { stores: [], managers: [], names: [] }); })
      .catch(() => { /* offline / not seeded yet — silently degrade */ });
    return () => { cancelled = true; };
  }, []);

  // Filter manager options to those whose store matches the picked store
  // (once one is chosen). Empty store → show every manager alphabetically.
  const managersForStore = useMemo(() => {
    const list = roster.managers || [];
    const q = store.trim().toLowerCase();
    if (!q) return list;
    const matched = list.filter((m) => (m.store || "").toLowerCase().includes(q) || (m.entity || "").toLowerCase().includes(q));
    // Fallback to the full list when no manager belongs to that store so
    // the vendor can still type a name freely.
    return matched.length ? matched : list;
  }, [roster, store]);

  // Track online / offline transitions + queued count
  useEffect(() => {
    const refreshCount = async () => setPendingCount(await queueCount());
    refreshCount();

    const onOnline = () => {
      setIsOnline(true);
      // Attempt to flush queue immediately
      syncPending().then((r) => {
        refreshCount();
        if (r.sent > 0) {
          toast.success(
            lang === "es"
              ? `Se sincronizaron ${r.sent} examen(es) guardados offline.`
              : `Synced ${r.sent} offline exam(s).`
          );
        }
      });
    };
    const onOffline = () => setIsOnline(false);
    const onSynced = () => refreshCount();

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("canales:offline-synced", onSynced);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("canales:offline-synced", onSynced);
    };
  }, [lang]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/exam/${slug}`);
        setExam(data);
        setFromCache(false);
        // Persist a copy for offline use
        saveExamCache(slug, data);
        // Dynamic page title for shared previews
        const title = lang === "es" ? data.title_es : data.title_en;
        document.title = `${title} · Muebleria Canales — Aprende, diviértete y gana dinero`;
      } catch (e) {
        // Network / server failure — try IndexedDB fallback
        const cached = await getExamCache(slug);
        if (cached) {
          setExam(cached);
          setFromCache(true);
          const title = lang === "es" ? cached.title_es : cached.title_en;
          document.title = `${title} · Muebleria Canales — Aprende, diviértete y gana dinero`;
        } else {
          setError(e?.response?.data?.detail || "Not found");
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      document.title = "Muebleria Canales — Aprende, diviértete y gana dinero";
    };
  }, [slug, lang]);

  const total = exam?.questions?.length || 0;
  const q = exam?.questions?.[current];

  useEffect(() => {
    if (!started || !exam?.time_limit_seconds) return;
    const end = Date.now() + exam.time_limit_seconds * 1000;
    setRemaining(exam.time_limit_seconds);
    timerRef.current = setInterval(() => {
      const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setRemaining(s);
      if (s <= 0) {
        clearInterval(timerRef.current);
        submit();
      }
    }, 500);
    return () => clearInterval(timerRef.current);
  }, [started, exam]);

  const start = () => {
    if (!name.trim()) {
      toast.error(lang === "es" ? "Escribe tu nombre" : "Enter your name");
      return;
    }
    if (!store.trim()) {
      toast.error(lang === "es" ? "Escribe tu tienda o corporación" : "Enter your store or corporation");
      return;
    }
    if (!managerName.trim()) {
      toast.error(lang === "es" ? "Escribe el nombre de tu manager" : "Enter your manager's name");
      return;
    }
    // Persist for next time so the vendor doesn't retype the store/manager.
    localStorage.setItem("canales_vendor_name", name.trim());
    localStorage.setItem("canales_vendor_store", store.trim());
    localStorage.setItem("canales_vendor_manager", managerName.trim());
    setStarted(true);
    setStartedAt(new Date().toISOString());
  };

  const advanceTimerRef = useRef(null);

  // Cancel any pending auto-advance (called from manual next/prev/submit).
  const cancelAutoAdvance = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };

  // Cleanup on unmount so timers never fire on a dead component.
  useEffect(() => cancelAutoAdvance, []);

  const pickAnswer = (qid, oid) => {
    setAnswers((a) => ({ ...a, [qid]: oid }));
    // Haptic feedback on supporting mobile devices (safe no-op on desktop).
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      try { navigator.vibrate(25); } catch { /* ignore */ }
    }
    // Auto-advance to the next question after 400ms so the exam flows
    // without extra taps. On the last question we DON'T advance — the
    // vendor has to tap "Finalizar" explicitly.
    cancelAutoAdvance();
    advanceTimerRef.current = setTimeout(() => {
      setCurrent((c) => (c < total - 1 ? c + 1 : c));
      advanceTimerRef.current = null;
    }, 400);
  };

  const submit = async () => {
    if (!exam) return;
    if (timerRef.current) clearInterval(timerRef.current);
    const payload = {
      employee_name: name,
      store: store,
      manager_name: managerName,
      started_at: startedAt,
      answers: exam.questions.map((qq) => ({
        question_id: qq.question_id,
        selected: answers[qq.question_id] || null,
        time_spent_ms: 0,
      })),
    };
    // Offline (or transient network failure) → queue and show a friendly screen.
    const goOfflineQueue = async () => {
      await enqueueSubmission({ slug, payload });
      setQueuedOffline(true);
      setPendingCount(await queueCount());
      toast.message(
        lang === "es"
          ? "Sin conexión: tu examen se guardó y se enviará al reconectar."
          : "Offline: your exam was saved and will be sent when you reconnect."
      );
    };

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      await goOfflineQueue();
      return;
    }

    try {
      const { data } = await api.post(`/public/exam/${slug}/submit`, payload);
      setResult(data);
    } catch (e) {
      // Treat network-level errors as offline. Server 4xx/5xx surface as toast.
      if (!e?.response) {
        await goOfflineQueue();
      } else {
        toast.error("Error");
      }
    }
  };

  const restart = () => {
    setStarted(false);
    setStartedAt(null);
    setAnswers({});
    setCurrent(0);
    setResult(null);
    setRemaining(null);
    setQueuedOffline(false);
  };

  const trySync = async () => {
    const r = await syncPending();
    setPendingCount(await queueCount());
    if (r.sent > 0) {
      toast.success(
        lang === "es"
          ? `Se sincronizaron ${r.sent} examen(es).`
          : `Synced ${r.sent} exam(s).`
      );
    } else if (r.attempted === 0) {
      toast.message(lang === "es" ? "No hay exámenes pendientes." : "No pending exams.");
    } else {
      toast.error(
        lang === "es"
          ? "No se pudo sincronizar. Verifica tu conexión."
          : "Sync failed. Check your connection."
      );
    }
  };

  const downloadCert = () => {
    if (!result) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    const NAVY = [13, 26, 63];       // #0D1A3F
    const NAVY_HEX = "#0D1A3F";
    const GOLD = [255, 209, 0];      // #FFD100
    const GOLD_HEX = "#FFD100";
    const CREAM = [255, 246, 217];   // #FFF6D9
    const RED = [227, 0, 15];        // #E3000F
    const INK = "#0B0B14";

    // Cream background
    doc.setFillColor(...CREAM);
    doc.rect(0, 0, W, H, "F");

    // LEFT decorative cascading curves (ellipses off screen)
    doc.setFillColor(...NAVY);
    doc.ellipse(-W * 0.05, H * 0.5, W * 0.28, H * 0.72, "F");
    doc.setFillColor(...GOLD);
    doc.ellipse(-W * 0.09, H * 0.5, W * 0.25, H * 0.62, "F");
    doc.setFillColor(...NAVY);
    doc.ellipse(-W * 0.14, H * 0.5, W * 0.22, H * 0.52, "F");
    doc.setFillColor(...GOLD);
    doc.ellipse(-W * 0.20, H * 0.5, W * 0.19, H * 0.42, "F");
    doc.setFillColor(...CREAM);
    doc.ellipse(-W * 0.24, H * 0.5, W * 0.16, H * 0.34, "F");

    // RIGHT decorative cascading curves (mirrored)
    doc.setFillColor(...NAVY);
    doc.ellipse(W * 1.05, H * 0.5, W * 0.28, H * 0.72, "F");
    doc.setFillColor(...GOLD);
    doc.ellipse(W * 1.09, H * 0.5, W * 0.25, H * 0.62, "F");
    doc.setFillColor(...NAVY);
    doc.ellipse(W * 1.14, H * 0.5, W * 0.22, H * 0.52, "F");
    doc.setFillColor(...GOLD);
    doc.ellipse(W * 1.20, H * 0.5, W * 0.19, H * 0.42, "F");
    doc.setFillColor(...CREAM);
    doc.ellipse(W * 1.24, H * 0.5, W * 0.16, H * 0.34, "F");

    // Halftone dots on visible navy areas (subtle brand texture)
    doc.setFillColor(255, 255, 255, 0.15);
    for (let x = 8; x < 90; x += 12) {
      for (let y = 60; y < H - 60; y += 12) {
        doc.circle(x, y, 0.9, "F");
        doc.circle(W - x, y, 0.9, "F");
      }
    }

    // TOP: Canales logo plate
    const px = W / 2 - 90;
    const py = 34;
    doc.setFillColor(...NAVY);
    doc.roundedRect(px, py, 180, 66, 8, 8, "F");
    // Roof squiggle
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(1.2);
    doc.lines(
      [[16, -3], [16, 3], [16, -3], [16, 3], [16, -3], [16, 3], [16, -3], [16, 3], [16, -3], [16, 3]],
      px + 20, py + 18
    );
    // CANALES
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...GOLD);
    doc.text("CANALES", W / 2, py + 38, { align: "center", charSpace: 2 });
    doc.setFontSize(15);
    doc.text("FURNITURE", W / 2, py + 56, { align: "center", charSpace: 2 });
    // Red underline
    doc.setFillColor(...RED);
    doc.rect(px + 30, py + 60, 120, 2, "F");

    // TITLE
    doc.setFont("helvetica", "bold");
    doc.setFontSize(40);
    doc.setTextColor(NAVY_HEX);
    doc.text(lang === "es" ? "CERTIFICADO" : "CERTIFICATE", W / 2, py + 118, { align: "center", charSpace: 3 });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(15);
    doc.text(lang === "es" ? "DE APROBACIÓN" : "OF ACHIEVEMENT", W / 2, py + 142, { align: "center", charSpace: 4 });

    // Ornament divider
    doc.setDrawColor(GOLD_HEX);
    doc.setLineWidth(1.5);
    doc.line(W / 2 - 90, py + 158, W / 2 - 14, py + 158);
    doc.line(W / 2 + 14, py + 158, W / 2 + 90, py + 158);
    doc.setFillColor(GOLD_HEX);
    doc.circle(W / 2, py + 158, 3.2, "F");
    doc.setFillColor(NAVY_HEX);
    doc.circle(W / 2, py + 158, 1.6, "F");

    // Presented to
    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(60, 60, 60);
    doc.text(
      lang === "es" ? "Este certificado se presenta con orgullo a" : "This certificate is proudly presented to",
      W / 2, py + 190, { align: "center" }
    );

    // Name in bold italic (times italic mimics script)
    doc.setFont("times", "bolditalic");
    doc.setFontSize(52);
    doc.setTextColor(NAVY_HEX);
    doc.text(name, W / 2, py + 244, { align: "center" });

    // Body text
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(70, 70, 70);
    const title = lang === "es" ? exam.title_es : exam.title_en;
    doc.text(
      (lang === "es" ? "Por completar exitosamente el " : "For successfully completing the ") + title,
      W / 2, py + 274, { align: "center" }
    );
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(NAVY_HEX);
    doc.text(
      `${result.score_correct} / ${result.score_total}  ·  ${result.score_percent}%  ·  ★ ${result.stars}/5`,
      W / 2, py + 296, { align: "center" }
    );

    // Bottom seal — scalloped gold circle with navy center
    const cx = W / 2;
    const cy = H - 82;
    // Scallop: 16 small bumps around
    doc.setFillColor(...GOLD);
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      doc.circle(cx + Math.cos(a) * 34, cy + Math.sin(a) * 34, 5, "F");
    }
    doc.circle(cx, cy, 34, "F");
    doc.setFillColor(...NAVY);
    doc.circle(cx, cy, 28, "F");
    // seal text
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(GOLD_HEX);
    doc.text(lang === "es" ? "CANALES" : "CANALES", cx, cy - 5, { align: "center", charSpace: 1 });
    doc.text(lang === "es" ? "APROBADO" : "AWARD", cx, cy + 3, { align: "center", charSpace: 1 });
    doc.setFontSize(6);
    doc.text("★ ★ ★ ★ ★", cx, cy + 11, { align: "center" });

    // Date + signature lines
    doc.setDrawColor(NAVY_HEX);
    doc.setLineWidth(0.8);
    doc.line(cx - 250, H - 62, cx - 90, H - 62);
    doc.line(cx + 90, H - 62, cx + 250, H - 62);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(NAVY_HEX);
    doc.text(new Date().toLocaleDateString(), cx - 170, H - 66, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(lang === "es" ? "Fecha" : "Date", cx - 170, H - 48, { align: "center" });
    doc.text(lang === "es" ? "Firma del Instructor" : "Instructor Signature", cx + 170, H - 48, { align: "center" });

    // corner brand strip
    doc.setFontSize(8);
    doc.setTextColor(NAVY_HEX);
    doc.text("canalesfurniture.com", W / 2, H - 22, { align: "center" });

    doc.save(`canales-certificate-${name}.pdf`);
    void INK; // avoid unused-var lint
  };

  if (loading) return <div className="min-h-screen bg-halftone flex items-center justify-center font-display">Loading…</div>;

  if (error) {
    return (
      <div className="min-h-screen bg-halftone flex items-center justify-center">
        <div className="brutal p-8 max-w-md text-center">
          <div className="font-display font-black text-2xl mb-2">404</div>
          <div className="font-body">{lang === "es" ? "Este examen no existe o fue desactivado." : "This exam doesn't exist or was disabled."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-halftone" data-testid={EXAM.root}>
      <header className="sticky top-0 z-40 bg-canales-yellow border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CanalesLogo width={120} />
            <div className="font-display font-black text-xs uppercase tracking-widest bg-black text-canales-yellow px-2 py-0.5 rounded">
              EXAM
            </div>
          </div>
          <div className="flex items-center gap-2">
            {remaining !== null && (
              <div className="flex items-center gap-2 brutal-sm px-3 py-1 bg-white font-display font-bold">
                <Timer className="w-4 h-4" /> {formatSecs(remaining)}
              </div>
            )}
            <LangToggle />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Offline / sync banner */}
        {(!isOnline || fromCache || pendingCount > 0) && (
          <div
            data-testid="offline-banner"
            className={`brutal-sm mb-4 p-3 flex items-center justify-between gap-3 ${
              !isOnline ? "bg-canales-yellow" : "bg-white"
            }`}
          >
            <div className="flex items-center gap-2 font-body text-sm">
              {!isOnline ? (
                <>
                  <WifiOff className="w-4 h-4" />
                  <span className="font-display font-bold">
                    {lang === "es" ? "Sin conexión" : "Offline"} —{" "}
                  </span>
                  <span>
                    {fromCache
                      ? lang === "es"
                        ? "usando examen guardado."
                        : "using cached exam."
                      : lang === "es"
                      ? "puedes seguir contestando."
                      : "you can keep answering."}
                  </span>
                </>
              ) : (
                <>
                  <CloudUpload className="w-4 h-4" />
                  <span>
                    {lang === "es"
                      ? `${pendingCount} examen(es) pendientes por sincronizar.`
                      : `${pendingCount} exam(s) pending to sync.`}
                  </span>
                </>
              )}
            </div>
            {isOnline && pendingCount > 0 && (
              <button
                onClick={trySync}
                data-testid="offline-sync-now"
                className="brutal-btn bg-canales-blue text-white px-3 py-1 text-xs font-display font-bold flex items-center gap-1"
              >
                <CloudUpload className="w-3 h-3" />
                {lang === "es" ? "Sincronizar ahora" : "Sync now"}
              </button>
            )}
          </div>
        )}

        {/* Queued-offline confirmation (replaces result screen when submitted offline) */}
        {queuedOffline && !result && (
          <div
            data-testid="offline-queued-screen"
            className="brutal p-6 bg-canales-cream text-center"
          >
            <CheckCircle2 className="w-14 h-14 mx-auto text-canales-blue" />
            <div className="mt-2 font-display font-black text-3xl">
              {lang === "es" ? "¡Guardado offline!" : "Saved offline!"}
            </div>
            <p className="mt-2 font-body">
              {lang === "es"
                ? "Tu examen se envió a la cola. Se sincronizará automáticamente cuando vuelvas a tener internet."
                : "Your exam is queued. It will sync automatically once you're back online."}
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <button
                onClick={trySync}
                data-testid="offline-queued-sync"
                className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
              >
                <CloudUpload className="w-4 h-4" />
                {lang === "es" ? "Intentar sincronizar" : "Try sync"}
              </button>
              <button
                onClick={restart}
                data-testid="offline-queued-restart"
                className="brutal-btn bg-white px-4 py-2"
              >
                {lang === "es" ? "Intentar de nuevo" : "Try again"}
              </button>
            </div>
          </div>
        )}

        {/* pre-start */}
        {!started && !result && !queuedOffline && (
          <div className="brutal p-8 bg-white text-center">
            <Mascot size={110} />
            <h1 className="mt-4 font-display font-black text-3xl">{lang === "es" ? exam.title_es : exam.title_en}</h1>
            <p className="mt-2 font-body">
              {total} {lang === "es" ? "preguntas" : "questions"} ·{" "}
              {exam.time_limit_seconds ? `${Math.round(exam.time_limit_seconds / 60)} ${t("minutes")}` : t("unlimited")} ·{" "}
              {t("passingScore")} {exam.passing_score}%
            </p>
            <div className="mt-6 max-w-sm mx-auto space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("yourName")}
                data-testid={EXAM.nameInput}
                autoComplete="name"
                className="w-full brutal-sm px-4 py-3 font-body text-lg"
              />
              <input
                type="text"
                value={store}
                onChange={(e) => setStore(e.target.value)}
                placeholder={lang === "es" ? "Tienda o corporación" : "Store or corporation"}
                data-testid="exam-store-input"
                list="exam-store-options"
                autoComplete="organization"
                className="w-full brutal-sm px-4 py-3 font-body text-lg"
              />
              {roster.stores?.length > 0 && (
                <datalist id="exam-store-options">
                  {roster.stores.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              )}
              <input
                type="text"
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
                placeholder={lang === "es" ? "Nombre de tu manager" : "Your manager's name"}
                data-testid="exam-manager-input"
                list="exam-manager-options"
                autoComplete="off"
                className="w-full brutal-sm px-4 py-3 font-body text-lg"
              />
              {managersForStore.length > 0 && (
                <datalist id="exam-manager-options">
                  {managersForStore.map((m) => (
                    <option
                      key={m.name}
                      value={m.name}
                      label={m.store || m.entity || ""}
                    />
                  ))}
                </datalist>
              )}
              <button
                onClick={start}
                data-testid={EXAM.startBtn}
                className="brutal-btn bg-canales-red text-white w-full px-6 py-3 text-lg"
              >
                {t("startExam")}
              </button>
            </div>
          </div>
        )}

        {/* in-progress */}
        {started && !result && !queuedOffline && q && (
          <div>
            <div className="mb-3 flex items-center gap-3">
              <div className="font-display font-bold">
                {current + 1} / {total}
              </div>
              <div className="flex-1 h-3 bg-white border-2 border-black rounded-full overflow-hidden">
                <div className="h-full bg-canales-blue" style={{ width: `${((current + 1) / total) * 100}%` }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={q.question_id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="brutal p-6 bg-white"
              >
                <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">
                  {q.topic}
                </div>
                <h3 className="mt-1 font-display font-black text-2xl">{q[`prompt_${lang}`]}</h3>
                <div className="mt-4 space-y-3">
                  {q.options.map((o, i) => {
                    const picked = answers[q.question_id] === o.id;
                    return (
                      <button
                        key={o.id}
                        data-testid={EXAM.option(i)}
                        onClick={() => pickAnswer(q.question_id, o.id)}
                        className={`w-full text-left p-4 sm:p-5 font-body text-base sm:text-lg flex items-center gap-3 transition-all duration-150 ${
                          picked
                            ? "bg-canales-blue text-white border-[4px] border-black shadow-[6px_6px_0_#000] translate-x-[-2px] translate-y-[-2px] scale-[1.01] font-bold"
                            : "bg-white border-[3px] border-black shadow-[3px_3px_0_#000] hover:bg-canales-cream active:bg-canales-yellow"
                        }`}
                      >
                        <span
                          className={`flex items-center justify-center w-9 h-9 rounded-full font-display font-black text-lg shrink-0 border-2 ${
                            picked
                              ? "bg-canales-yellow text-black border-black"
                              : "bg-canales-cream text-canales-red border-canales-red"
                          }`}
                        >
                          {picked ? <CheckCircle2 className="w-6 h-6" /> : o.id}
                        </span>
                        <span className="flex-1">{o[lang]}</span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex justify-between">
              <button
                onClick={() => { cancelAutoAdvance(); setCurrent((c) => Math.max(0, c - 1)); }}
                disabled={current === 0}
                className="brutal-btn bg-white px-4 py-2 disabled:opacity-40"
              >
                {t("previous")}
              </button>
              {current < total - 1 ? (
                <button
                  onClick={() => { cancelAutoAdvance(); setCurrent((c) => c + 1); }}
                  data-testid={EXAM.next}
                  className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
                >
                  {t("next")} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => { cancelAutoAdvance(); submit(); }}
                  data-testid={EXAM.finish}
                  className="brutal-btn bg-canales-red text-white px-4 py-2"
                >
                  {t("finish")}
                </button>
              )}
            </div>
          </div>
        )}

        {/* result */}
        {result && (
          <div>
            <div className={`brutal p-6 text-center ${result.passed ? "bg-green-50" : "bg-red-50"}`}>
              <Trophy className={`w-14 h-14 mx-auto ${result.passed ? "text-green-700" : "text-canales-red"}`} />
              <div className="mt-2 font-display font-black text-3xl">
                {result.passed ? t("passed") : t("failed")}
              </div>
              <div className="mt-1 font-display font-bold text-xl">
                {result.score_correct} / {result.score_total} · {result.score_percent}%
              </div>
              <div className="mt-2 inline-flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-8 h-8 ${i < result.stars ? "fill-canales-yellow text-black" : "text-neutral-300"}`} />
                ))}
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <div
                  data-testid="exam-admin-cert-note"
                  className="brutal-sm bg-canales-cream px-4 py-2 text-sm font-body text-neutral-700 max-w-md"
                >
                  <span className="font-display font-black text-canales-red">
                    {lang === "es" ? "🏆 ¡Bien hecho!" : "🏆 Well done!"}
                  </span>{" "}
                  {lang === "es"
                    ? "Tu certificado lo emite el admin de Canales cuando completes toda la ruta con 90%+."
                    : "Your certificate will be issued by the Canales admin once you complete the full route at 90%+."}
                </div>
                <button onClick={restart} data-testid={EXAM.restart} className="brutal-btn bg-white px-4 py-2">
                  {lang === "es" ? "Intentar de nuevo" : "Try again"}
                </button>
              </div>
            </div>

            {(result.badges?.length > 0 || result.tier) && (
              <div className="mt-4 brutal p-5 bg-canales-yellow">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs font-display font-bold uppercase tracking-widest">
                      {t("yourTier")}
                    </div>
                    {result.tier && <TierChip tier={result.tier} lang={lang} />}
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-display font-bold uppercase tracking-widest">
                      {lang === "es" ? "Promedio general" : "Overall avg"}
                    </div>
                    <div className="font-display font-black text-2xl">{result.employee_avg}%</div>
                  </div>
                </div>
                {result.badges?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-display font-bold uppercase tracking-widest mb-2">
                      {t("yourBadges")}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {result.badges.map((b) => (
                        <BadgePill key={b.code} badge={b} lang={lang} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6">
              <h3 className="font-display font-black text-xl mb-2">
                {lang === "es" ? "Retroalimentación de Carlos" : "Feedback from Carlos"}
              </h3>
              {result.feedback.map((item) => (
                <AvatarMentorFeedback key={item.question_id} item={item} lang={lang} t={t} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
